import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  getFilesListQueryLimitMax,
  getGetFilesListQueryKey,
  useGetDepartmentsList,
  useGetFilesListHook,
  useListOrganizations,
} from '@/api/trafikportalen';
import { VirtualizedList } from '@/components/VirtualizedList.tsx';
import {
  FileListItem,
  FileListItemData,
} from '@/components/Reports/FileListItem.tsx';
import { toApiDateTimeRange } from '@/helpers/formatting';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { useSearchFilterSort } from '@/hooks/useSearchFilterSort';
import { Route as FilesRoute } from '@/routes/$tenantId/_layout/reports/$view';
import { createSortConfig } from '@/utils/sort';
import { Loading } from '../Loading';
import { RightPanel } from '../RightPanel/RightPanel';
import { FilterPanel } from '../FilterPanel';
import Input from '../core/Input';
import { Dropdown } from '../core/Dropdown';
import { checkUserPermissions } from '../Auth/checkUserPermissions';
import { FilterType } from '@/types/filterSearch';
import type { OptionsType } from '../core/Dropdown';
import type { GetFilesListParams } from '@/api/trafikportalen';

const getFirstSearchId = (value: string | string[] | undefined) =>
  Array.isArray(value) ? value[0] : value;

export const FilesList = () => {
  const { formatMessage } = useIntl();
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const [announcement, setAnnouncement] = useState('');
  const isSysadmin = checkUserPermissions({ permission: 'tenant:viewer' });
  const getFilesList = useGetFilesListHook();

  const {
    query,
    dateFrom: filterDateFrom,
    dateTo: filterDateTo,
    department: filterDepartment,
  } = FilesRoute.useSearch();

  const selectedEntityId = getFirstSearchId(filterDepartment);
  const searchQuery = query?.trim();

  const filesListParams = useMemo((): GetFilesListParams => {
    const { from, to } = toApiDateTimeRange(filterDateFrom, filterDateTo);

    return {
      limit: getFilesListQueryLimitMax,
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      ...(!isSysadmin && selectedEntityId
        ? { department_id: selectedEntityId }
        : {}),
      ...(searchQuery ? { name: searchQuery } : {}),
    };
  }, [filterDateFrom, filterDateTo, isSysadmin, selectedEntityId, searchQuery]);

  const {
    items: files,
    isLoading,
    isFetchingNextPage,
    fetchNextPageIfNeeded,
  } = usePaginatedList({
    fetchPage: getFilesList,
    params: filesListParams,
    queryKey: getGetFilesListQueryKey(filesListParams),
  });

  const { data: organizations } = useListOrganizations({
    query: {
      enabled: isSysadmin,
    },
  });

  const { data: departments } = useGetDepartmentsList({
    query: {
      enabled: !isSysadmin,
    },
  });

  const sortConfigs = useMemo(() => {
    return createSortConfig<FileListItemData>({
      textFields: ['created_at'],
    });
  }, []);

  const sortedFiles = useMemo((): FileListItemData[] => {
    if (!files) return [];
    const rootDepartmentId = departments?.find((d) => !d.parent_id)?.id;
    return files
      .map((file) => {
        const relevantDepartments =
          departments?.filter((d) => file.department_ids.includes(d.id)) || [];

        // Only apply fallback for non-sysadmin users with empty department_ids
        const departmentIds =
          !isSysadmin && file.department_ids.length === 0 && rootDepartmentId
            ? [rootDepartmentId]
            : file.department_ids;

        return {
          ...file,
          department_ids: departmentIds,
          departmentNames: relevantDepartments?.map((d) => d.name),
          organizationName: organizations?.find(
            (o) => o.id === file.organization_id,
          )?.name,
        };
      })
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
  }, [departments, files, organizations, isSysadmin]);

  const {
    filterSearch,
    sortedItems,
    setFilterSearch,
    handleSearchParams,
    handleSearch,
  } = useSearchFilterSort<FileListItemData>({
    items: sortedFiles || [],
    searchKeys: [],
    sortConfigs,
    routePath: FilesRoute.fullPath,
    defaultSearchParams: {
      department: [],
      dateFrom: '',
      dateTo: '',
    },
    searchDebounceMs: 300,
  });

  const entityOptions = useMemo(() => {
    if (isSysadmin && organizations) {
      return organizations
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((org) => ({
          id: org.id,
          label: org.name,
        }));
    }
    if (!isSysadmin && departments) {
      return departments
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((dept) => ({
          id: dept.id,
          label: dept.name,
        }));
    }
    return [];
  }, [isSysadmin, organizations, departments]);

  const filteredFiles = useMemo(() => {
    if (!isSysadmin || !selectedEntityId) {
      return sortedItems;
    }

    return sortedItems.filter(
      (file) => file.organization_id === selectedEntityId,
    );
  }, [isSysadmin, selectedEntityId, sortedItems]);

  const onEntityChange = useCallback(
    (value: OptionsType) => {
      const department = value ? [value as FilterType] : [];

      setFilterSearch((prev) => ({
        ...prev!,
        department,
      }));

      handleSearchParams({
        department: department.map((v) => v.id),
      });
    },
    [handleSearchParams, setFilterSearch],
  );

  const onInputChange = useCallback(
    (value: string, filterType: 'dateFrom' | 'dateTo') => {
      setFilterSearch((prev) => ({
        ...prev!,
        [filterType]: value,
      }));

      handleSearchParams({
        [filterType]: value,
      });
    },
    [handleSearchParams, setFilterSearch],
  );

  const renderFilesListItem = useCallback((file: FileListItemData) => {
    return <FileListItem key={file.id} file={file} />;
  }, []);

  useEffect(() => {
    if (filterSearch || !entityOptions.length) {
      return;
    }

    const entityId = getFirstSearchId(filterDepartment);
    const entityFilter = entityOptions.find((opt) => opt.id === entityId);

    setFilterSearch({
      query: query ?? '',
      dateFrom: filterDateFrom ?? '',
      dateTo: filterDateTo ?? '',
      department: entityFilter ? [entityFilter] : [],
    });
  }, [
    filterDateFrom,
    filterDateTo,
    filterDepartment,
    entityOptions,
    filterSearch,
    setFilterSearch,
    query,
  ]);

  const entityLabel = useMemo(() => {
    if (isSysadmin) {
      return formatMessage({ defaultMessage: 'Organisation' });
    }
    return formatMessage({ defaultMessage: 'Avdelning' });
  }, [isSysadmin, formatMessage]);

  const entityAriaDescription = useMemo(() => {
    if (isSysadmin) {
      return formatMessage({
        defaultMessage: 'Filtrera filer genom att välja organisation',
      });
    }
    return formatMessage({
      defaultMessage: 'Filtrera filer genom att välja avdelning',
    });
  }, [isSysadmin, formatMessage]);

  // Announce changes to filter for screen readers
  useEffect(() => {
    if (!filterSearch) return;

    const count = filteredFiles.length;
    const message = formatMessage(
      {
        defaultMessage:
          '{count, plural, one {En fil visas} other {{count} filer visas}}',
      },
      { count },
    );

    setAnnouncement(message);
  }, [filterSearch, filteredFiles.length, formatMessage]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
      <RightPanel ref={rightPanelRef}>
        {filterSearch && (
          <FilterPanel
            firstInput={
              <Input
                type="text"
                label={formatMessage({ defaultMessage: 'Sök' })}
                placeholder={formatMessage({
                  defaultMessage: 'Sök filnamn',
                })}
                id="file-search"
                value={filterSearch.query ?? ''}
                onChange={(e) => handleSearch(e.target.value)}
              />
            }
            filterOptions={[
              <Dropdown
                key={entityOptions.length}
                label={entityLabel}
                id="entity-filter"
                options={entityOptions}
                placeholder={formatMessage({ defaultMessage: 'Alla' })}
                value={filterSearch.department?.[0]}
                onValueChange={onEntityChange}
                aria-description={entityAriaDescription}
                containerRef={rightPanelRef}
              />,
              <Input
                type="date"
                label={formatMessage({ defaultMessage: 'Från datum' })}
                id="date-from"
                value={filterSearch.dateFrom || ''}
                onChange={(e) => onInputChange(e.target.value, 'dateFrom')}
                aria-description={formatMessage({
                  defaultMessage: 'Filtrera filer från detta datum',
                })}
              />,
              <Input
                type="date"
                label={formatMessage({ defaultMessage: 'Till datum' })}
                id="date-to"
                value={filterSearch.dateTo || ''}
                onChange={(e) => onInputChange(e.target.value, 'dateTo')}
                aria-description={formatMessage({
                  defaultMessage: 'Filtrera filer till detta datum',
                })}
              />,
            ]}
          />
        )}
      </RightPanel>
      <VirtualizedList
        count={filteredFiles?.length}
        countName={formatMessage({ defaultMessage: 'filer' })}
        totalItems={filteredFiles}
        filteredItems={filteredFiles}
        renderItem={renderFilesListItem}
        getItemKey={(file) => file.id}
        onNearEnd={fetchNextPageIfNeeded}
        isLoadingMore={isFetchingNextPage}
      />
    </>
  );
};
